/* eslint-disable react/no-children-prop */
import React, { useEffect, useState } from 'react';
import './AssetsEmptyState.less';
import { Button, Tooltip, Upload } from 'antd';
import { useAssetsUppy } from '@context/AssetsUppyContext';
import LottieComponent from '@components/Lottie';
import { ReactComponent as GoogleDriveSvg } from '@assets/icons/google-drive.svg';
import { ReactComponent as DropBoxSvg } from '@assets/icons/dropbox.svg';
import { ReactComponent as BoxSvg } from '@assets/icons/box.svg';
import { UppyRemotePickerModal } from '@components/Modals/UppyRemotePickerModal/UppyRemotePickerModal';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useSearchParams } from 'react-router-dom';
import { CampaignItemDto } from '@api/Api';

function AssetsEmptyState({
  campaign,
  folderId,
  isSearchEmptyState
}: {
  campaign?: CampaignItemDto;
  folderId?: string;
  isSearchEmptyState?: boolean;
}) {
  const uppy = useAssetsUppy();
  const [searchParams, setSearchParams] = useSearchParams();
  const { onStartTour, tourData } = useOnboardingProcess();
  const onAddFile = async (data: any) => {
    if (!campaign?.id) return;
    if (tourData?.type) onStartTour({ type: undefined });
    uppy.addFile({
      name: data.file.name,
      type: data.file.type,
      data: data.file,
      meta: {
        relativePath: new Date().toISOString(),
        campaignId: campaign.id,
        folderId: folderId ?? null
      }
    });
    await uppy.upload();
    if (tourData?.type === 'empty-files') {
      searchParams.set('startTour', 'upload-asset-member');
      setSearchParams(searchParams);
    }
  };
  const [activeRemote, setActiveRemote] = useState<string | null>(null);

  const startTour = searchParams.get('startTour') ?? undefined;
  useEffect(() => {
    if (startTour !== 'empty-files') return;
    onStartTour({ type: startTour as any });
  }, [startTour]);

  const showUpload =
    !isSearchEmptyState && !!campaign?.permissions.createAssets;
  return showUpload ? (
    <>
      <Upload
        multiple
        showUploadList={false}
        customRequest={(data) => {
          onAddFile(data);
        }}
        className="assets_empty_state_upload_area"
      >
        <div className="assets_empty_state">
          <div className="block tour-files-empty-state">
            <LottieComponent className="icon" view="upload" />

            <p>
              Drag & drop files or folders,{' '}
              <Button
                type="link"
                style={{ padding: 0, textDecoration: 'underline' }}
              >
                browse files
              </Button>{' '}
              or import from:
            </p>
            <div
              style={{ display: 'flex', gap: 10 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip
                placement="bottom"
                overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                title="Google Drive"
              >
                <Button
                  aria-label="Google Drive"
                  type="ghost"
                  style={{ padding: 10 }}
                  onClick={() => setActiveRemote('GoogleDrive')}
                >
                  <GoogleDriveSvg height={24} />
                </Button>
              </Tooltip>
              <Tooltip
                placement="bottom"
                overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                title="Box"
              >
                <Button
                  aria-label="Box"
                  type="ghost"
                  style={{ padding: 10 }}
                  onClick={() => setActiveRemote('Box')}
                >
                  <BoxSvg height={24} />
                </Button>
              </Tooltip>
              <Tooltip
                placement="bottom"
                overlayClassName="toggle_favorite_overlay toggle_favorite_overlay--small"
                title="Dropbox"
              >
                <Button
                  aria-label="Dropbox"
                  type="ghost"
                  style={{ padding: 10 }}
                  onClick={() => setActiveRemote('Dropbox')}
                >
                  <DropBoxSvg height={24} />
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      </Upload>
      <UppyRemotePickerModal
        uppy={uppy}
        activeRemote={activeRemote}
        onClose={() => setActiveRemote(null)}
        onFilesAdded={(files) => {
          if (!campaign?.id) return;
          if (tourData?.type === 'empty-files') {
            onStartTour({ type: undefined });
            searchParams.set('startTour', 'upload-asset-member');
            setSearchParams(searchParams);
          }
          for (const file of files || []) {
            file.meta.relativePath = new Date().toISOString();
            file.meta.campaignId = campaign.id;
            file.meta.folderId = folderId;
            uppy.setFileMeta(file.id, file.meta);
          }
          uppy.upload();
        }}
      />
    </>
  ) : (
    <div className="assets_empty_state assets_empty_state--search">
      <div className="block">
        <LottieComponent className="icon" view="noSearchingResult" />
        <strong
          className="main-body-text main-body-text--bold"
          style={{ marginBottom: 8 }}
        >
          No results found
        </strong>
        <p className="main-body-text main-body-text--dark-gray">
          It seems we can’t find any results based on your search
        </p>
      </div>
    </div>
  );
}

export default AssetsEmptyState;
